import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Briefcase,
  Sparkles,
  GraduationCap,
  Loader2,
  Users,
  ChevronRight,
  Save,
  Search,
} from 'lucide-react'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import Textarea from '@/components/common/Textarea'
import ProspectCard from '@/components/recruiter/prospects/ProspectCard'
import ProspectPipelineStats from '@/components/recruiter/prospects/ProspectPipelineStats'
import { useJobProspects } from '@/hooks/useJobProspects'
import { toast } from '@/stores/toastStore'
import recruiterJobService from '@/services/recruiterJobService'
import { RecruiterJob, EmploymentType, WorkMode, RecruiterJobStatus } from '@/types'

const MIN_DESCRIPTION_LENGTH = 50

const employmentTypeOptions = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INTERNSHIP', label: 'Internship' },
]

const workModeOptions = [
  { value: 'REMOTE', label: 'Remote' },
  { value: 'HYBRID', label: 'Hybrid' },
  { value: 'ONSITE', label: 'Onsite' },
]

const currencyOptions = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'TND', label: 'TND (د.ت)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'AUD', label: 'AUD ($)' },
]

const statusOptions = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ARCHIVED', label: 'Archived' },
]

interface JobFormData {
  title: string
  description: string
  position: string
  location: string
  employmentType: EmploymentType
  workMode: WorkMode
  salaryMin: number | undefined
  salaryMax: number | undefined
  salaryCurrency: string
  benefits: string
  responsibilities: string
  requirements: string
  status: RecruiterJobStatus
  defaultExpiryDays: number | undefined
}

function jobToFormData(job: RecruiterJob): JobFormData {
  return {
    title: job.title,
    description: job.description,
    position: job.position || '',
    location: job.location || '',
    employmentType: job.employmentType || 'FULL_TIME',
    workMode: job.workMode || 'HYBRID',
    salaryMin: job.salaryMin ?? undefined,
    salaryMax: job.salaryMax ?? undefined,
    salaryCurrency: job.salaryCurrency || 'TND',
    benefits: job.benefits || '',
    responsibilities: job.responsibilities || '',
    requirements: job.requirements || '',
    status: job.status,
    defaultExpiryDays: job.defaultExpiryDays || 14,
  }
}

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('jobOffers')

  const [job, setJob] = useState<RecruiterJob | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [reprocessing, setReprocessing] = useState(false)
  const [formData, setFormData] = useState<JobFormData | null>(null)
  const [offerStatusFilter, setOfferStatusFilter] = useState<string | undefined>(undefined)

  const { data: prospectsData, isLoading: prospectsLoading } = useJobProspects(
    {
      recruiterJobId: jobId || '',
      offerStatus: offerStatusFilter,
      page: 1,
      limit: 100,
    },
    !!jobId
  )

  const deduplicatedProspects = useMemo(() => {
    if (!prospectsData?.prospects) return []
    const grouped = new Map<
      string,
      {
        prospect: (typeof prospectsData.prospects)[number]
        offers: { id: string; status: string; createdAt: string }[]
      }
    >()
    for (const p of prospectsData.prospects) {
      const existing = grouped.get(p.candidateId)
      const offer = p.offer ? [p.offer] : []
      const extraOffers = p.offers ?? []
      const allOffers = [...offer, ...extraOffers]
      if (!existing) {
        grouped.set(p.candidateId, { prospect: p, offers: allOffers })
      } else {
        existing.offers.push(...allOffers)
        if (p.updatedAt > existing.prospect.updatedAt) {
          existing.prospect = p
        }
      }
    }
    return Array.from(grouped.values())
  }, [prospectsData])

  useEffect(() => {
    if (!jobId) return
    recruiterJobService
      .getJob(jobId)
      .then((data) => {
        setJob(data)
        setFormData(jobToFormData(data))
      })
      .catch(() => {
        toast.error('Failed to load job')
        navigate('/recruiter/jobs')
      })
      .finally(() => setLoading(false))
  }, [jobId, navigate])

  const handleSave = async () => {
    if (!job || !formData) return
    if (!formData.title || !formData.description) {
      toast.error(t('jobs.form.titleRequired', 'Title and description are required'))
      return
    }
    if (formData.description.trim().length < MIN_DESCRIPTION_LENGTH) {
      toast.error(t('jobs.form.descriptionTooShort', 'Description must be at least 50 characters'))
      return
    }
    const descriptionChanged = formData.description !== job.description
    try {
      setSubmitting(true)
      const updated = await recruiterJobService.updateJob(job.id, {
        title: formData.title,
        description: formData.description,
        position: formData.position || null,
        location: formData.location || null,
        employmentType: formData.employmentType,
        workMode: formData.workMode,
        salaryMin: formData.salaryMin ?? null,
        salaryMax: formData.salaryMax ?? null,
        salaryCurrency: formData.salaryCurrency,
        benefits: formData.benefits || null,
        responsibilities: formData.responsibilities || null,
        requirements: formData.requirements || null,
        status: formData.status,
        defaultExpiryDays: formData.defaultExpiryDays ?? null,
      })
      setJob(updated)
      if (descriptionChanged) {
        toast.success(
          t(
            'jobs.success.updatedWithAI',
            'Job updated. AI will re-process insights in the background.'
          )
        )
      } else {
        toast.success(t('jobs.success.updated', 'Job updated successfully'))
      }
    } catch (error: unknown) {
      const rawError = (error as { response?: { data?: { error?: unknown } } }).response?.data
        ?.error
      let errorMessage: string
      if (typeof rawError === 'string') {
        errorMessage = rawError
      } else if (rawError && typeof rawError === 'object') {
        const zodErr = rawError as { issues?: Array<{ message: string }> }
        errorMessage =
          zodErr.issues?.[0]?.message ?? t('jobs.errors.updateFailed', 'Failed to update job')
      } else {
        errorMessage = t('jobs.errors.updateFailed', 'Failed to update job')
      }
      toast.error(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleReprocess = async () => {
    if (!job) return
    try {
      setReprocessing(true)
      const updated = await recruiterJobService.reprocessJob(job.id)
      setJob(updated)
      toast.success(t('jobs.success.reprocessed', 'AI insights refreshed successfully'))
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } }
      toast.error(
        err.response?.data?.error || t('jobs.errors.reprocessFailed', 'Failed to reprocess job')
      )
    } finally {
      setReprocessing(false)
    }
  }

  const set = (field: keyof JobFormData, value: string | number | undefined) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!job || !formData) return null

  const hasAiData = job.embeddingProcessedAt !== null
  const hasExtractedDetails = !!(
    job.extractedExperienceYears != null ||
    job.extractedEducationLevel ||
    (job.extractedSkills && job.extractedSkills.length > 0)
  )

  return (
    <div className="container-custom py-8">
      {/* Back navigation + action buttons */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => navigate('/recruiter/jobs')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('jobs.list.backToJobs', 'Back to Jobs')}
          </Button>
          <Link
            to={`/recruiter/ai-matching/create?jobId=${job.id}`}
            className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            AI Match
          </Link>
        </div>
        <Button onClick={handleSave} disabled={submitting} className="flex items-center gap-2">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('jobs.form.updateButton', 'Save Changes')}
        </Button>
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">{job.title}</h1>
      {job.position && <p className="text-gray-500 mb-6">{job.position}</p>}

      <div className="flex gap-6 items-start">
        {/* Left column — job info + edit form */}
        <div className="flex-1 min-w-0">
          {/* AI Extracted Insights */}
          <div
            className={`p-4 rounded-xl mb-8 border ${hasAiData ? 'bg-purple-50 border-purple-200' : 'bg-amber-50 border-amber-200'}`}
          >
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Sparkles
                  className={`w-4 h-4 ${hasAiData ? 'text-purple-600' : 'text-amber-600'}`}
                />
                <h2
                  className={`text-sm font-semibold ${hasAiData ? 'text-purple-800' : 'text-amber-800'}`}
                >
                  {hasAiData ? 'AI Extracted Insights' : 'AI Processing Pending'}
                </h2>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleReprocess}
                disabled={reprocessing || submitting || job?.embeddingStatus !== 'FAILED'}
                className="flex items-center gap-1 text-xs"
              >
                {reprocessing ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3" />
                )}
                {reprocessing ? 'Processing…' : 'Re-run AI'}
              </Button>
            </div>

            {!hasAiData ? (
              <p className="text-xs text-amber-700">
                AI analysis has not completed yet. Save the job or click &quot;Re-run AI&quot; to
                extract insights.
              </p>
            ) : hasExtractedDetails ? (
              <>
                <div className="flex flex-wrap gap-4 text-sm text-gray-700 mb-3">
                  {job.extractedExperienceYears != null && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-3.5 h-3.5 text-purple-500" />
                      {job.extractedExperienceYears}+ years experience
                    </span>
                  )}
                  {job.extractedEducationLevel && (
                    <span className="flex items-center gap-1">
                      <GraduationCap className="w-3.5 h-3.5 text-purple-500" />
                      {job.extractedEducationLevel}
                    </span>
                  )}
                </div>
                {job.extractedSkills && job.extractedSkills.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 rounded-full text-sm font-semibold bg-purple-600 text-white border border-purple-700">
                        {job.extractedSkills[0]}
                      </span>
                      <span className="text-xs text-purple-700 font-medium">Primary Skill</span>
                    </div>
                    {job.extractedSkills.length > 1 && (
                      <div className="flex flex-wrap gap-1.5">
                        {job.extractedSkills.slice(1).map((skill) => (
                          <span
                            key={skill}
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-purple-600">
                Processed — no structured data extracted yet.
              </p>
            )}
          </div>

          {/* Edit Form */}
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('jobs.form.titleLabel', 'Job Title')} <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder={t('jobs.form.titlePlaceholder', 'e.g., Senior Software Engineer')}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('jobs.form.descriptionLabel', 'Description')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder={t(
                  'jobs.form.descriptionPlaceholder',
                  'Full job description (min. 50 characters)...'
                )}
                rows={5}
              />
              <p
                className={`mt-1 text-xs ${formData.description.trim().length < MIN_DESCRIPTION_LENGTH ? 'text-amber-600' : 'text-gray-400'}`}
              >
                {formData.description.trim().length < MIN_DESCRIPTION_LENGTH
                  ? `${formData.description.trim().length} / ${MIN_DESCRIPTION_LENGTH} characters minimum`
                  : `${formData.description.trim().length} characters`}
              </p>
            </div>

            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.positionLabel')}
              </label>
              <Input
                type="text"
                value={formData.position}
                onChange={(e) => set('position', e.target.value)}
                placeholder={t('templates.form.positionPlaceholder')}
                icon={<Briefcase className="w-5 h-5" />}
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.locationLabel')}
              </label>
              <Input
                type="text"
                value={formData.location}
                onChange={(e) => set('location', e.target.value)}
                placeholder={t('templates.form.locationPlaceholder')}
              />
            </div>

            {/* Employment Type & Work Mode */}
            <div className="grid grid-cols-2 gap-4">
              <Select
                label={t('templates.form.employmentTypeLabel')}
                value={formData.employmentType}
                onChange={(e) => set('employmentType', e.target.value as EmploymentType)}
                options={employmentTypeOptions}
              />
              <Select
                label={t('templates.form.workModeLabel')}
                value={formData.workMode}
                onChange={(e) => set('workMode', e.target.value as WorkMode)}
                options={workModeOptions}
              />
            </div>

            {/* Salary Range */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('templates.form.salaryMinLabel')}
                </label>
                <Input
                  type="number"
                  value={formData.salaryMin || ''}
                  onChange={(e) =>
                    set('salaryMin', e.target.value ? Number(e.target.value) : undefined)
                  }
                  placeholder={t('templates.form.salaryMinPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('templates.form.salaryMaxLabel')}
                </label>
                <Input
                  type="number"
                  value={formData.salaryMax || ''}
                  onChange={(e) =>
                    set('salaryMax', e.target.value ? Number(e.target.value) : undefined)
                  }
                  placeholder={t('templates.form.salaryMaxPlaceholder')}
                />
              </div>
              <Select
                label={t('templates.form.currencyLabel')}
                value={formData.salaryCurrency}
                onChange={(e) => set('salaryCurrency', e.target.value)}
                options={currencyOptions}
              />
            </div>

            {/* Responsibilities */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.responsibilitiesLabel')}
              </label>
              <Textarea
                value={formData.responsibilities}
                onChange={(e) => set('responsibilities', e.target.value)}
                placeholder={t('templates.form.responsibilitiesPlaceholder')}
                rows={3}
              />
            </div>

            {/* Requirements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.requirementsLabel')}
              </label>
              <Textarea
                value={formData.requirements}
                onChange={(e) => set('requirements', e.target.value)}
                placeholder={t('templates.form.requirementsPlaceholder')}
                rows={3}
              />
            </div>

            {/* Benefits */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.benefitsLabel')}
              </label>
              <Textarea
                value={formData.benefits}
                onChange={(e) => set('benefits', e.target.value)}
                placeholder={t('templates.form.benefitsPlaceholder')}
                rows={3}
              />
            </div>

            {/* Status */}
            <Select
              label={t('jobs.form.statusLabel', 'Status')}
              value={formData.status}
              onChange={(e) => set('status', e.target.value as RecruiterJobStatus)}
              options={statusOptions}
            />

            {/* Default Expiry Days */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.expiryDaysLabel', 'Default Expiry Days')}
              </label>
              <Input
                type="number"
                min="1"
                max="365"
                value={formData.defaultExpiryDays?.toString() || ''}
                onChange={(e) =>
                  set('defaultExpiryDays', e.target.value ? parseInt(e.target.value) : undefined)
                }
                placeholder={t('templates.form.expiryDaysPlaceholder')}
              />
              <p className="text-sm text-gray-500 mt-1">{t('templates.form.expiryDaysHint')}</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={submitting}
                className="flex-1"
              >
                {submitting
                  ? t('templates.form.updatingButton')
                  : t('jobs.form.updateButton', 'Save Changes')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate('/recruiter/jobs')}
                disabled={submitting}
              >
                {t('templates.form.cancelButton')}
              </Button>
            </div>
          </div>
        </div>
        {/* end left column */}

        {/* Right column — prospects sidebar (40%) */}
        <div className="w-[40%] flex-shrink-0">
          <div
            className="sticky top-6 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-sm flex flex-col"
            style={{ maxHeight: 'calc(100vh - 48px)' }}
          >
            {/* Sidebar header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-gray-800">Prospects Pipeline</span>
                {job.prospectsCount !== undefined && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {job.prospectsCount}
                  </span>
                )}
              </div>
              <Link
                to={`/recruiter/jobs/${job.id}/prospects`}
                className="flex items-center gap-0.5 text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                View all <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Pipeline stats */}
            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <ProspectPipelineStats
                activeOfferStatusFilter={offerStatusFilter}
                onOfferStatusFilterChange={setOfferStatusFilter}
              />
            </div>

            {/* Prospects list */}
            <div className="flex-grow overflow-y-auto p-3 space-y-2" style={{ minHeight: '340px' }}>
              {prospectsLoading ? (
                <div className="flex justify-center items-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              ) : deduplicatedProspects.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 mb-4">
                    {offerStatusFilter
                      ? 'No prospects with this offer status.'
                      : 'No prospects yet.'}
                  </p>
                  {!offerStatusFilter && (
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Search className="w-3.5 h-3.5" />}
                        onClick={() => navigate('/recruiter/search')}
                      >
                        Search Candidates
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        icon={<Sparkles className="w-3.5 h-3.5" />}
                        onClick={() => navigate(`/recruiter/ai-matching/create?jobId=${job.id}`)}
                      >
                        AI Matching
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {deduplicatedProspects.map(({ prospect, offers }) => (
                    <ProspectCard
                      key={prospect.candidateId}
                      prospect={prospect}
                      allOffers={offers}
                      jobId={job.id}
                    />
                  ))}
                  {prospectsData &&
                    prospectsData.pagination.total > deduplicatedProspects.length && (
                      <Link
                        to={`/recruiter/jobs/${job.id}/prospects`}
                        className="block text-center text-xs text-blue-600 hover:text-blue-800 py-2 font-medium"
                      >
                        View all {prospectsData.pagination.total} prospects →
                      </Link>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* end flex row */}
    </div>
  )
}
